import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Breadcrumb from "@/components/ui/breadcrumb";
import Button from "@/components/ui/button";
import Chip from "@/components/ui/chip";
import Input from "@/components/ui/input";
import Dropdown from "@/components/ui/dropdown";
import DatePicker from "@/components/ui/datepicker";
import MapComponent from "@/components/ui/map";
import ConfirmModal from "@/components/ui/confirm-modal";
import DepartmentAccordion from "../components/DepartmentAccordion";
import AddPersonModal from "../components/AddPersonModal";
import {
  LeftChevron,
  Close,
  Check,
  Plus,
  Delete,
  CardView,
} from "@/components/icons";
import type { FilterField, TableColumn } from "@/types/component";
import {
  type DepartmentWithPersonnel,
  type PersonnelRequestEntry,
  type PersonnelRequestStatus,
  type ApproveWithClassGroupsPayload,
  type PlanningGroupFromApi,
  type PlanningScheduledTime,
  getPlanningGroupVirtualClassroomId,
} from "../types/request-and-approval.types";
import { requestAndApprovalApi } from "../services/request-and-approval.api";
import { virtualClassroomsApi } from "@/features/education-catalog/services/virtual-classrooms.api";
import { educationCatalogApi } from "@/features/education-catalog/services/education-catalog.api";
import { resolvePlanningGroupVirtualClassroomId } from "../utils/resolvePlanningGroupVirtualClassroomId";
import { normalizeClassroomDateRange } from "../utils/normalizeClassroomDateRange";
import { weekdaysInRange } from "@/features/client-educations/utils/scheduled-times";
import { sanitizeJitsiRoomName } from "@/utils";
import EditVirtualClassroomModal from "../components/EditVirtualClassroomModal";
import VirtualClassroomsListModal from "../components/VirtualClassroomsListModal";
import styles from "./EducationManagerPage.module.scss";
import { usePermission, useIsEducationManagerOf } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import { useStore } from "@/store";
import { UserRoleType } from "@/features/roles-and-permissions/types/roles-and-permissions.types";
import { educationManagementApi } from "@/features/education-management/services/education-management.api";
import type {
  EducationDetailApi,
  PresetLocationApi,
} from "@/features/education-management/types/education-management.types";
import { EDUCATION_TYPE_API } from "@/features/education-management/types/education-management.types";

type PlanningGroup = {
  id: string;
  name: string;
  jitsiRoomName: string;
  dateRange: [Date | null, Date | null];
  scheduledTimes: Array<PlanningScheduledTime & { id: string }>;
  presetLocationId: string;
  location: string;
  latitude: string;
  longitude: string;
  enrollmentIds: string[];
  instructorIds: string[];
};

const getStatusChipType = (
  status: PersonnelRequestStatus,
): "success" | "warning" | "error" => {
  if (status === "onaylandi") return "success";
  if (status === "reddedildi") return "error";
  return "warning";
};

const EducationManagerPage: React.FC = () => {
  const { t } = useTranslation();
  const STATUS_LABELS: Record<PersonnelRequestStatus, string> = {
    onaylandi: t("educationOfficer.statusLabels.onaylandi"),
    ilk_onay_bekliyor: t("educationOfficer.statusLabels.ilk_onay_bekliyor"),
    ikinci_onay_bekliyor: t("educationOfficer.statusLabels.ikinci_onay_bekliyor"),
    ucuncu_onay_bekliyor: t("educationOfficer.statusLabels.ucuncu_onay_bekliyor"),
    reddedildi: t("educationOfficer.statusLabels.reddedildi"),
    iptal_edildi: t("educationOfficer.statusLabels.iptal_edildi"),
    tamamlandi: t("educationOfficer.statusLabels.tamamlandi"),
  };
  const navigate = useNavigate();
  const { educationId } = useParams<{ educationId: string }>();
  const userType = useStore((s) => s.userType);
  const { hasPermission } = usePermission();
  const canAddPerson = hasPermission(PERMISSIONS.REQUEST_AND_APPROVAL_CREATE);
  const canViewLocations = hasPermission(PERMISSIONS.LOCATIONS_VIEW);
  const location = useLocation();
  const educationName =
    (location.state as { educationName?: string } | null)?.educationName ||
    t("educationManager.defaultEducationName");

  const [departments, setDepartments] = useState<DepartmentWithPersonnel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [selectedRows, setSelectedRows] = useState<PersonnelRequestEntry[]>([]);
  const [filterValues, setFilterValues] = useState<
    Record<string, PersonnelRequestStatus | PersonnelRequestStatus[]>
  >({});
  const [isAddPersonOpen, setIsAddPersonOpen] = useState(false);
  const [isConfirmSendOpen, setIsConfirmSendOpen] = useState(false);
  const [isConfirmRejectOpen, setIsConfirmRejectOpen] = useState(false);
  const [isSendLoading, setIsSendLoading] = useState(false);
  const [planningDepartments, setPlanningDepartments] = useState<
    DepartmentWithPersonnel[]
  >([]);
  const [planningSelectedRows, setPlanningSelectedRows] = useState<
    PersonnelRequestEntry[]
  >([]);
  const [planningGroups, setPlanningGroups] = useState<PlanningGroup[]>([]);
  const [presetLocations, setPresetLocations] = useState<PresetLocationApi[]>(
    [],
  );
  const [isPlanningLoading, setIsPlanningLoading] = useState(false);
  const [isPlanningSubmitting, setIsPlanningSubmitting] = useState(false);
  const [savingGroupId, setSavingGroupId] = useState<string | null>(null);
  const [educationType, setEducationType] = useState<number | null>(null);
  const [educationDetail, setEducationDetail] =
    useState<EducationDetailApi | null>(null);
  const [educationDetailLoading, setEducationDetailLoading] = useState(
    () => Boolean(educationId),
  );
  // Eğitime per-education yönetici olarak atanmış EDUCATION_SUPERVISOR de
  // bu eğitim üzerinde EDUCATION_MANAGER ile aynı yetkilere sahip olmalı.
  // educationDetail.managers backend'den geliyor; null iken false döner ve
  // detail yüklenince doğru değere ayarlanır.
  const isAssignedManagerOfThisEducation = useIsEducationManagerOf(educationDetail);
  const isEducationManager =
    userType === UserRoleType.EDUCATION_MANAGER || isAssignedManagerOfThisEducation;
  const canDeleteEnrollment = isEducationManager;
  const [pendingDeleteRow, setPendingDeleteRow] =
    useState<PersonnelRequestEntry | null>(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [editVirtualClassTarget, setEditVirtualClassTarget] = useState<{
    group: PlanningGroupFromApi;
    virtualClassroomId: string;
  } | null>(null);
  const [savedPlanningGroups, setSavedPlanningGroups] = useState<
    PlanningGroupFromApi[]
  >([]);
  const [isSavedPlanningGroupsLoading, setIsSavedPlanningGroupsLoading] =
    useState(false);
  const [isVirtualClassroomsModalOpen, setIsVirtualClassroomsModalOpen] =
    useState(false);
  const [educationInstructorOptions, setEducationInstructorOptions] = useState<
    { label: string; value: string }[]
  >([]);
  /** Eğitime şu an atanmış eğitmen ID'leri — sınıfa atanan eğitmenden eksik
   *  olanları kayıttan önce eğitime ekleyip BE 400'ü engellemek için diff. */
  const [educationInstructorIdSet, setEducationInstructorIdSet] = useState<
    Set<string>
  >(new Set());

  useEffect(() => {
    if (!educationId) {
      setEducationInstructorOptions([]);
      setEducationInstructorIdSet(new Set());
      return;
    }
    let cancelled = false;

    // Tüm domain eğitmenleri — dropdown seçenekleri (sınıf eğitmen havuzu artık
    // eğitime atanmışlarla sınırlı değil).
    void educationCatalogApi
      .getInstructors()
      .then((items) => {
        if (cancelled) return;
        setEducationInstructorOptions(
          items.map((i) => ({
            value: i.id,
            label:
              [i.firstName, i.lastName].filter(Boolean).join(" ").trim() ||
              i.id,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setEducationInstructorOptions([]);
      });

    // Eğitime atanmış eğitmenler — diff hesabı için.
    void educationCatalogApi
      .listEducationInstructors(educationId)
      .then((items) => {
        if (cancelled) return;
        setEducationInstructorIdSet(new Set(items.map((i) => i.userID)));
      })
      .catch(() => {
        if (!cancelled) setEducationInstructorIdSet(new Set());
      });

    return () => {
      cancelled = true;
    };
  }, [educationId]);

  /**
   * Sınıfa atanan eğitmenler eğitime de atanmış olmak zorunda (BE 400 döner).
   * Bu helper diff hesaplayıp eksikleri eğitim eğitmen listesine ekler.
   */
  const ensureInstructorsInEducation = useCallback(
    async (allInstructorIds: string[]): Promise<void> => {
      if (!educationId || allInstructorIds.length === 0) return;
      const toAdd = allInstructorIds.filter(
        (id) => !educationInstructorIdSet.has(id),
      );
      if (toAdd.length === 0) return;
      await educationCatalogApi.addEducationInstructors(educationId, toAdd);
      setEducationInstructorIdSet((prev) => {
        const next = new Set(prev);
        for (const id of toAdd) next.add(id);
        return next;
      });
    },
    [educationId, educationInstructorIdSet],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchDepartments = useCallback(async () => {
    if (!educationId) return;
    setIsLoading(true);
    try {
      const response = await requestAndApprovalApi.getDepartmentsByEducationId(
        educationId,
        {
          status: filterValues.status,
          search: debouncedSearch || undefined,
        },
      );
      setDepartments(response.data);
      setExpandedIds((prev) =>
        prev.length > 0
          ? prev
          : response.data[0]?.id
            ? [response.data[0].id]
            : [],
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching departments:", error);
    } finally {
      setIsLoading(false);
    }
  }, [educationId, filterValues, debouncedSearch]);

  const fetchPlanningDepartments = useCallback(async () => {
    if (!educationId || !isEducationManager) return;
    setIsPlanningLoading(true);
    try {
      // Sınıf oluşturma akışında personel kök Başkanlık / İşletme altında
      // gruplanır — yaprak bölüm akordeonu kafa karıştırıyordu.
      const response =
        await requestAndApprovalApi.getRootDepartmentsByEducationId(
          educationId,
          {
            status: "ucuncu_onay_bekliyor",
            search: debouncedSearch || undefined,
          },
        );
      setPlanningDepartments(response.data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching planning departments:", error);
    } finally {
      setIsPlanningLoading(false);
    }
  }, [educationId, debouncedSearch, isEducationManager]);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  useEffect(() => {
    fetchPlanningDepartments();
  }, [fetchPlanningDepartments]);

  useEffect(() => {
    // Planning artık preset lokasyon zorunlu — LOCATIONS_VIEW permission'ına
    // bakılmaksızın fetch edilir. Backend 403 dönerse catch'e düşer ve boş kalır.
    educationManagementApi
      .getLocations({ page: 1, limit: 100 })
      .then((res) => setPresetLocations(res?.data ?? []))
      .catch(() => setPresetLocations([]));
  }, []);

  useEffect(() => {
    if (!educationId) {
      setEducationDetailLoading(false);
      return;
    }
    let cancelled = false;
    setEducationDetailLoading(true);
    setEducationType(null);
    setEducationDetail(null);
    educationManagementApi
      .getById(educationId)
      .then(({ data }) => {
        if (cancelled) return;
        // Manager (planlama) sayfası gate: strict EDUCATION_SUPERVISOR yalnızca
        // eğitime per-education yönetici olarak atanmışsa erişebilir. Backend
        // detail endpoint'i officer görünürlüğü için "kendi departmanından
        // başvuru yapılan eğitim"e de izin veriyor; manager sayfası bu gevşek
        // izni kabul etmemeli.
        const { userType: currentUserType, currentUser } = useStore.getState();
        if (currentUserType === UserRoleType.EDUCATION_SUPERVISOR) {
          const myId = currentUser?.id;
          const isAssignedManager = (data.managers ?? []).some(
            (m) => m.userID && m.userID === myId,
          );
          if (!isAssignedManager) {
            navigate("/dashboard/unauthorized", { replace: true });
            return;
          }
        }
        setEducationType(data.type);
        setEducationDetail(data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Atanmamış EDUCATION_SUPERVISOR backend'den 403 alır → manager
        // sayfasını görme yetkisi yok.
        const status = (err as { response?: { status?: number } } | null)
          ?.response?.status;
        if (status === 403) {
          navigate("/dashboard/unauthorized", { replace: true });
          return;
        }
        setEducationType(null);
        setEducationDetail(null);
      })
      .finally(() => {
        if (!cancelled) setEducationDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [educationId, navigate]);

  const fetchSavedPlanningGroups = useCallback(async () => {
    if (!educationId) return;
    setIsSavedPlanningGroupsLoading(true);
    try {
      const [{ groups }, virtualClassrooms] = await Promise.all([
        requestAndApprovalApi.getPlanningGroups(educationId),
        virtualClassroomsApi.listByEducation(educationId).catch(() => []),
      ]);
      const merged = (groups ?? []).map((g) => {
        const resolved = resolvePlanningGroupVirtualClassroomId(
          g,
          virtualClassrooms,
        );
        if (!resolved) return g;
        return {
          ...g,
          virtualClassroomId: g.virtualClassroomId ?? resolved,
        };
      });
      setSavedPlanningGroups(merged);
    } catch {
      setSavedPlanningGroups([]);
    } finally {
      setIsSavedPlanningGroupsLoading(false);
    }
  }, [educationId]);

  useEffect(() => {
    if (!educationId || !isEducationManager || educationDetailLoading) return;
    if (
      educationType !== EDUCATION_TYPE_API.ON_SITE &&
      educationType !== EDUCATION_TYPE_API.ONLINE
    ) {
      setSavedPlanningGroups([]);
      return;
    }
    fetchSavedPlanningGroups();
  }, [
    educationId,
    isEducationManager,
    educationDetailLoading,
    educationType,
    fetchSavedPlanningGroups,
  ]);

  const handleBack = () => {
    navigate("/dashboard/request-and-approval");
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleFilterChange = (filters: Record<string, unknown>) => {
    setFilterValues(
      filters as Record<
        string,
        PersonnelRequestStatus | PersonnelRequestStatus[]
      >,
    );
  };

  // Gün numaraları backend ile birebir: 0=Pazar … 6=Cumartesi (JS Date.getDay()
  // ile aynı). Backend scheduledTimes.day alanını @Min(0) @Max(6) ile doğruluyor;
  // Pazar'ı 7 göndermek 400 veriyordu. Pzt–Cmt (1–6) iki tarafta da aynı.
  const dayOptions = [
    { label: t("dayLabels.monday"), value: "1" },
    { label: t("dayLabels.tuesday"), value: "2" },
    { label: t("dayLabels.wednesday"), value: "3" },
    { label: t("dayLabels.thursday"), value: "4" },
    { label: t("dayLabels.friday"), value: "5" },
    { label: t("dayLabels.saturday"), value: "6" },
    { label: t("dayLabels.sunday"), value: "0" },
  ];

  const presetLocationOptions = presetLocations.map((loc) => ({
    label: `${loc.name} — ${loc.address}`,
    value: loc.id,
  }));

  /** Yeni form + API'den gelen kayıtlı sınıfların enrollment id'leri */
  const groupAssignedEnrollmentIds = useMemo(() => {
    const ids = new Set<string>();
    planningGroups.forEach((group) => {
      group.enrollmentIds.forEach((id) => ids.add(id));
    });
    savedPlanningGroups.forEach((group) => {
      (group.enrollmentIds ?? []).forEach((id) => ids.add(id));
    });
    return ids;
  }, [planningGroups, savedPlanningGroups]);

  const availablePlanningDepartments = useMemo(() => {
    return planningDepartments.map((dept) => ({
      ...dept,
      personnel: dept.personnel.filter(
        (p) => !groupAssignedEnrollmentIds.has(p.id),
      ),
      talepCount: dept.personnel.filter(
        (p) => !groupAssignedEnrollmentIds.has(p.id),
      ).length,
      onayCount: 0,
    }));
  }, [planningDepartments, groupAssignedEnrollmentIds]);

  /** Hiçbir sınıfa atanmamış bekleyenler — Dışarıda Bırakılanlar önizlemesi */
  const unassignedNotInAnyClass = useMemo(() => {
    const list: PersonnelRequestEntry[] = [];
    planningDepartments.forEach((dept) => {
      dept.personnel.forEach((p) => {
        if (!groupAssignedEnrollmentIds.has(p.id)) {
          list.push(p);
        }
      });
    });
    return list;
  }, [planningDepartments, groupAssignedEnrollmentIds]);

  const planningPersonnelMap = useMemo(() => {
    const map = new Map<string, PersonnelRequestEntry>();
    planningDepartments.forEach((dept) => {
      dept.personnel.forEach((p) => map.set(p.id, p));
    });
    return map;
  }, [planningDepartments]);

  const handlePlanningRowSelect = (row: PersonnelRequestEntry) => {
    setPlanningSelectedRows((prev) => {
      const isSelected = prev.some((r) => r.id === row.id);
      if (isSelected) return prev.filter((r) => r.id !== row.id);
      return [...prev, row];
    });
  };

  const handlePlanningDepartmentSelectAll = (
    department: DepartmentWithPersonnel,
    checked: boolean,
  ) => {
    setPlanningSelectedRows((prev) => {
      const deptIds = new Set(department.personnel.map((p) => p.id));
      if (checked) {
        const toAdd = department.personnel.filter(
          (p) => !prev.some((r) => r.id === p.id),
        );
        return [...prev, ...toAdd];
      }
      return prev.filter((r) => !deptIds.has(r.id));
    });
  };

  const handleAddGroup = () => {
    setPlanningGroups((prev) => [
      ...prev,
      {
        id: `group-${Date.now()}`,
        name: "",
        jitsiRoomName: "",
        dateRange: [null, null],
        scheduledTimes: [],
        presetLocationId: "",
        location: "",
        latitude: "",
        longitude: "",
        enrollmentIds: [],
        instructorIds: [],
      },
    ]);
  };

  const handleRemoveGroup = (groupId: string) => {
    setPlanningGroups((prev) => prev.filter((g) => g.id !== groupId));
  };

  const handleAddSelectedToGroup = (groupId: string) => {
    if (planningSelectedRows.length === 0) return;
    setPlanningGroups((prev) =>
      prev.map((group) => {
        if (group.id !== groupId) return group;
        const existing = new Set(group.enrollmentIds);
        const selectedIds = planningSelectedRows.map((r) => r.id);
        const uniqueToAdd = selectedIds.filter((id) => !existing.has(id));
        const nextCount = group.enrollmentIds.length + uniqueToAdd.length;
        if (nextCount > 20) {
          toast.error(t("educationManager.validation.maxPerClass"));
          return group;
        }
        return {
          ...group,
          enrollmentIds: [...group.enrollmentIds, ...uniqueToAdd],
        };
      }),
    );
    setPlanningSelectedRows([]);
  };

  const handleRemoveFromGroup = (groupId: string, enrollmentId: string) => {
    setPlanningGroups((prev) =>
      prev.map((group) =>
        group.id === groupId
          ? {
              ...group,
              enrollmentIds: group.enrollmentIds.filter(
                (id) => id !== enrollmentId,
              ),
            }
          : group,
      ),
    );
  };

  const updateGroup = (
    groupId: string,
    updater: (group: PlanningGroup) => PlanningGroup,
  ) => {
    setPlanningGroups((prev) =>
      prev.map((group) => (group.id === groupId ? updater(group) : group)),
    );
  };

  const handleAddScheduledTime = (groupId: string) => {
    updateGroup(groupId, (group) => ({
      ...group,
      scheduledTimes: [
        ...group.scheduledTimes,
        {
          id: `time-${Date.now()}`,
          day: 1,
          startTime: "",
          endTime: "",
          timezone: "Europe/Istanbul",
        },
      ],
    }));
  };

  const handleRemoveScheduledTime = (groupId: string, timeId: string) => {
    updateGroup(groupId, (group) => ({
      ...group,
      scheduledTimes: group.scheduledTimes.filter((t) => t.id !== timeId),
    }));
  };

  const handleUpdateScheduledTime = (
    groupId: string,
    timeId: string,
    patch: Partial<PlanningScheduledTime>,
  ) => {
    updateGroup(groupId, (group) => ({
      ...group,
      scheduledTimes: group.scheduledTimes.map((t) =>
        t.id === timeId ? { ...t, ...patch } : t,
      ),
    }));
  };

  // Eğitimin başlangıç/bitiş aralığı dışına çıkan sınıf tarihlerini engelle.
  const validateGroupWithinEducation = (
    start: Date | null,
    end: Date | null,
  ): string | null => {
    // Karşılaştırmayı gün bazında yapıyoruz: sınıf startDate'i gün başı (00:00)
    // kabul edilir, eğitim startDate'i de gün başına indirgenir; bitiş için
    // her iki tarafı gün sonuna (23:59:59.999) çekiyoruz. Aksi halde aynı
    // gün seçildiğinde TZ dönüşümleri yüzünden bir kaç saatlik fark
    // "sınıf eğitim başlangıcından önce" hatasına yol açabiliyor.
    const toDayStart = (d: Date): Date => {
      const copy = new Date(d);
      copy.setHours(0, 0, 0, 0);
      return copy;
    };
    const toDayEnd = (d: Date): Date => {
      const copy = new Date(d);
      copy.setHours(23, 59, 59, 999);
      return copy;
    };
    const eduStart = educationDetail?.startDate
      ? toDayStart(new Date(educationDetail.startDate))
      : null;
    const eduEnd = educationDetail?.endDate
      ? toDayEnd(new Date(educationDetail.endDate))
      : null;
    const classStart = start ? toDayStart(start) : null;
    const classEnd = end ? toDayEnd(end) : null;
    if (classStart && eduStart && classStart < eduStart) {
      return `Sınıf başlangıç tarihi eğitim başlangıcından (${eduStart.toLocaleDateString("tr-TR")}) önce olamaz.`;
    }
    if (classEnd && eduEnd && classEnd > eduEnd) {
      return `Sınıf bitiş tarihi eğitim bitişinden (${eduEnd.toLocaleDateString("tr-TR")}) sonra olamaz.`;
    }
    return null;
  };

  const validateSingleGroup = (group: PlanningGroup): string | null => {
    if (group.enrollmentIds.length === 0) {
      return t("educationManager.validation.minOnePerClass");
    }
    if (group.enrollmentIds.length > 20) {
      return t("educationManager.validation.maxPerClass");
    }
    if (
      educationType === EDUCATION_TYPE_API.ONLINE &&
      !group.jitsiRoomName.trim()
    ) {
      return t("educationManager.validation.jitsiRoomRequired");
    }
    const [start, end] = group.dateRange;
    if (start && end && start > end) {
      return t("educationManager.validation.endBeforeStart");
    }
    const eduErr = validateGroupWithinEducation(start, end);
    if (eduErr) return eduErr;
    // Seçilen haftalık günlerden biri sınıfın tarih aralığına hiç denk gelmiyorsa
    // kursiyer ekranında o gün için somut oturum üretilmez ("oturum planlanmadı").
    // Bunu kayıt öncesi engelle.
    const scheduledDays = group.scheduledTimes
      .filter((tm) => tm.startTime && tm.endTime)
      .map((tm) => Number(tm.day));
    if (scheduledDays.length > 0 && start && end) {
      const present = weekdaysInRange(start, end);
      if (scheduledDays.some((d) => !present.has(d))) {
        return t("educationManager.scheduleDayOutOfRange");
      }
    }
    if (
      educationType !== EDUCATION_TYPE_API.ONLINE &&
      !group.presetLocationId.trim()
    ) {
      return t("educationManager.validation.locationRequired");
    }
    return null;
  };

  const buildGroupPayload = (
    group: PlanningGroup,
  ): ApproveWithClassGroupsPayload["groups"][number] => {
    const [start, end] = group.dateRange;
    const { startDateIso, endDateIso } = normalizeClassroomDateRange(start, end);
    const scheduledTimes = group.scheduledTimes
      .filter((t) => t.startTime && t.endTime)
      .map((t) => ({
        day: Number(t.day),
        startTime: t.startTime,
        endTime: t.endTime,
      }));
    const hasPreset = group.presetLocationId.trim().length > 0;
    const jitsiTrim = group.jitsiRoomName.trim();
    return {
      name: group.name.trim() || undefined,
      ...(jitsiTrim ? { jitsiRoomName: jitsiTrim } : {}),
      presetLocationId: hasPreset ? group.presetLocationId : undefined,
      location: hasPreset ? undefined : group.location.trim() || undefined,
      latitude: hasPreset ? undefined : Number(group.latitude) || undefined,
      longitude: hasPreset ? undefined : Number(group.longitude) || undefined,
      startDate: startDateIso,
      endDate: endDateIso,
      scheduledTimes: scheduledTimes.length > 0 ? scheduledTimes : undefined,
      enrollmentIds: group.enrollmentIds,
      instructorIds:
        group.instructorIds.length > 0 ? group.instructorIds : undefined,
    };
  };

  const handleSaveSingleGroup = async (group: PlanningGroup) => {
    if (!educationId) return;
    const err = validateSingleGroup(group);
    if (err) {
      toast.error(err);
      return;
    }
    setSavingGroupId(group.id);
    try {
      // Sınıfa atanan eğitmenler eğitime de atanmış olmak zorunda.
      await ensureInstructorsInEducation(group.instructorIds);
      await requestAndApprovalApi.approveWithClassGroups(educationId, {
        groups: [buildGroupPayload(group)],
      });
      toast.success(t("educationManager.toastSuccess.groupSaved"));
      // Draft'ı kaldır, saved groups'u yenile
      setPlanningGroups((prev) => prev.filter((g) => g.id !== group.id));
      setPlanningSelectedRows((prev) =>
        prev.filter((r) => !group.enrollmentIds.includes(r.id)),
      );
      fetchPlanningDepartments();
      fetchSavedPlanningGroups();
    } catch {
      toast.error(t("educationManager.toastError.groupSaveFailed"));
    } finally {
      setSavingGroupId(null);
    }
  };

  const handleFinalizePlanning = async () => {
    if (!educationId) return;
    const groupsWithMembers = planningGroups.filter(
      (g) => g.enrollmentIds.length > 0,
    );
    const hasSaved = savedPlanningGroups.length > 0;
    if (groupsWithMembers.length === 0 && !hasSaved) {
      toast.error(t("educationManager.validation.atLeastOnePerson"));
      return;
    }

    for (const group of groupsWithMembers) {
      const err = validateSingleGroup(group);
      if (err) {
        toast.error(err);
        return;
      }
    }

    setIsPlanningSubmitting(true);
    try {
      // Tüm draft gruplardaki eğitmenleri tek atımda eğitime ekle.
      const allInstructorIds = Array.from(
        new Set(groupsWithMembers.flatMap((g) => g.instructorIds)),
      );
      await ensureInstructorsInEducation(allInstructorIds);
      await requestAndApprovalApi.finalizePlanning(educationId);
      toast.success(t("educationManager.toastSuccess.educationPublished"));
      setPlanningSelectedRows([]);
      setPlanningGroups([]);
      fetchPlanningDepartments();
      fetchSavedPlanningGroups();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error finalizing planning:", error);
      toast.error(t("educationManager.toastError.educationPublishFailed"));
    } finally {
      setIsPlanningSubmitting(false);
    }
  };

  const handleToggleExpand = (id: string) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const handleRowSelect = (row: PersonnelRequestEntry) => {
    setSelectedRows((prev) => {
      const isSelected = prev.some((r) => r.id === row.id);
      if (isSelected) return prev.filter((r) => r.id !== row.id);
      return [...prev, row];
    });
  };

  const handleDepartmentSelectAll = (
    department: DepartmentWithPersonnel,
    checked: boolean,
  ) => {
    setSelectedRows((prev) => {
      const selectablePersonnel = department.personnel.filter(
        (p) =>
          p.status === "ilk_onay_bekliyor" ||
          p.status === "ikinci_onay_bekliyor" ||
          p.status === "ucuncu_onay_bekliyor",
      );
      const deptIds = new Set(selectablePersonnel.map((p) => p.id));
      if (checked) {
        const toAdd = selectablePersonnel.filter(
          (p) => !prev.some((r) => r.id === p.id),
        );
        return [...prev, ...toAdd];
      }
      return prev.filter((r) => !deptIds.has(r.id));
    });
  };

  const removeFromCart = (id: string) => {
    setSelectedRows((prev) => prev.filter((r) => r.id !== id));
  };

  const handleAddPerson = () => {
    setIsAddPersonOpen(true);
  };

  const handleClickSendForApproval = () => {
    setIsConfirmSendOpen(true);
  };
  const handleClickReject = () => {
    setIsConfirmRejectOpen(true);
  };

  const handleConfirmSendForApproval = async () => {
    if (selectedRows.length === 0) return;
    setIsSendLoading(true);
    try {
      await requestAndApprovalApi.bulkReviewEnrollments({
        enrollmentIds: selectedRows.map((r) => r.id),
        isApproved: true,
      });
      setIsConfirmSendOpen(false);
      setSelectedRows([]);
      fetchDepartments();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error sending for approval:", error);
    } finally {
      setIsSendLoading(false);
    }
  };

  const handleConfirmReject = async () => {
    if (selectedRows.length === 0) return;
    setIsSendLoading(true);
    try {
      await requestAndApprovalApi.bulkReviewEnrollments({
        enrollmentIds: selectedRows.map((r) => r.id),
        isApproved: false,
      });
      setIsConfirmRejectOpen(false);
      setSelectedRows([]);
      fetchDepartments();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error rejecting requests:", error);
    } finally {
      setIsSendLoading(false);
    }
  };

  const handleConfirmDeleteEnrollment = async () => {
    if (!educationId || !pendingDeleteRow) return;
    setIsDeleteLoading(true);
    try {
      await requestAndApprovalApi.deleteEnrollment(
        educationId,
        pendingDeleteRow.id,
      );
      toast.success(
        t("educationManager.deleteEnrollmentSuccess"),
      );
      setPendingDeleteRow(null);
      setSelectedRows((prev) => prev.filter((r) => r.id !== pendingDeleteRow.id));
      fetchDepartments();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error deleting enrollment:", error);
      toast.error(
        t("educationManager.deleteEnrollmentError"),
      );
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const managerTableColumns = useMemo((): TableColumn<PersonnelRequestEntry>[] => {
    const base: TableColumn<PersonnelRequestEntry>[] = [
      {
        header: t("educationOfficer.columns.personnelName"),
        accessor: "personelAdi",
        className: "personnel-name-column",
      },
      {
        header: t("educationOfficer.columns.tcKimlikNo"),
        accessor: "tcKimlikNo",
        className: "tc-column",
      },
      {
        header: t("educationOfficer.columns.unvan"),
        accessor: "unvan",
        className: "unvan-column",
      },
      {
        header: t("educationOfficer.columns.status"),
        accessor: (row) => (
          <Chip type={getStatusChipType(row.status)} variant="soft" size="small">
            {STATUS_LABELS[row.status]}
          </Chip>
        ),
        className: "status-column",
      },
    ];
    if (!canDeleteEnrollment) return base;
    return [
      ...base,
      {
        header: t("educationManager.deleteEnrollmentColumn"),
        accessor: (row) => (
          <button
            type="button"
            className={styles.deleteEnrollmentButton}
            onClick={() => setPendingDeleteRow(row)}
            aria-label={t("educationManager.deleteEnrollmentAction")}
          >
            <Delete width={18} height={18} color="currentColor" />
          </button>
        ),
        className: "actions-column",
      },
    ];
  }, [canDeleteEnrollment, t, STATUS_LABELS]);

  const filterFields: FilterField[] = [
    {
      name: "status",
      label: t("educationOfficer.filters.status"),
      type: "dropdown",
      placeholder: t("educationOfficer.filters.statusPlaceholder"),
      options: [{ value: "onaylandi", label: t("educationManager.statusLabels.onaylandi") }],
    },
  ];

  const breadcrumbItems = [
    {
      label: t("educationOfficer.breadcrumb.requestAndApproval"),
      onClick: handleBack,
    },
    {
      label: `${educationName} ${t("educationOfficer.breadcrumb.requestsSuffix")}`,
    },
  ];

  const showEducationManagerTypeLoading =
    isEducationManager && educationDetailLoading;
  const isOnSiteOrOnlineType =
    educationType === EDUCATION_TYPE_API.ON_SITE ||
    educationType === EDUCATION_TYPE_API.ONLINE;
  /** Yüz yüze / canlı: sadece final planlama; ünite/ünitesiz: sadece üst akış */
  const showMainWorkflowSection =
    !isEducationManager ||
    (!educationDetailLoading &&
      !isOnSiteOrOnlineType &&
      (educationType === EDUCATION_TYPE_API.UNIT ||
        educationType === EDUCATION_TYPE_API.NONUNIT ||
        educationType === null));
  const showFinalizePlanningSection =
    isEducationManager &&
    !educationDetailLoading &&
    isOnSiteOrOnlineType;

  // Stats: departments'tan hesaplanır
  const requestStats = useMemo(() => {
    let total = 0;
    let approved = 0;
    let pending = 0;
    let rejected = 0;
    for (const dept of departments) {
      for (const p of dept.personnel) {
        total++;
        if (p.status === "onaylandi" || p.status === "tamamlandi") approved++;
        else if (p.status === "reddedildi" || p.status === "iptal_edildi")
          rejected++;
        else pending++;
      }
    }
    return { total, approved, pending, rejected };
  }, [departments]);

  // Eğitim tipi label
  const educationTypeLabel = useMemo(() => {
    if (educationType === EDUCATION_TYPE_API.ON_SITE)
      return t("requestAndApproval.educationTypes.faceToFace");
    if (educationType === EDUCATION_TYPE_API.ONLINE)
      return t("requestAndApproval.educationTypes.onlineLong");
    if (educationType === EDUCATION_TYPE_API.UNIT)
      return t("requestAndApproval.educationTypes.unit");
    if (educationType === EDUCATION_TYPE_API.NONUNIT)
      return t("requestAndApproval.educationTypes.nonUnit");
    return null;
  }, [educationType, t]);

  // Tarih aralığı formatlama
  const educationDateRange = useMemo(() => {
    if (!educationDetail) return null;
    const fmt = (iso?: string) => {
      if (!iso) return null;
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return null;
      return d.toLocaleDateString("tr-TR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    };
    const from = fmt(educationDetail.startDate);
    const to = fmt(educationDetail.endDate);
    if (from && to) return `${from} – ${to}`;
    return from ?? to ?? null;
  }, [educationDetail]);

  // Workflow stepper için mevcut adım
  const currentStep: 1 | 2 | 3 = showFinalizePlanningSection ? 3 : 2;

  return (
    <DashboardLayout>
      <Header title={t("requestAndApproval.title")} />

      <div className={styles.breadcrumbContainer}>
        <button
          type="button"
          className={styles.backButton}
          onClick={handleBack}
          aria-label={t("common.back")}
        >
          <LeftChevron width={20} height={20} />
        </button>
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Hero: eğitim context bandı */}
      <section className={styles.hero}>
        <div className={styles.heroMain}>
          <div className={styles.heroTitleBlock}>
            <h1 className={styles.heroTitle}>
              {educationDetail?.name || educationName || "—"}
            </h1>
            <div className={styles.heroMetaRow}>
              {educationTypeLabel && (
                <span className={`${styles.heroChip} ${styles.heroChipType}`}>
                  {educationTypeLabel}
                </span>
              )}
              {educationDetail?.categories?.[0]?.name && (
                <span
                  className={`${styles.heroChip} ${styles.heroChipCategory}`}
                >
                  {educationDetail.categories[0].name}
                </span>
              )}
              {educationDetail?.isRequired && (
                <span
                  className={`${styles.heroChip} ${styles.heroChipRequired}`}
                >
                  {t("clientHero.mandatory")}
                </span>
              )}
              {educationDateRange && (
                <span className={styles.heroMetaItem}>
                  📅 {educationDateRange}
                </span>
              )}
              {typeof educationDetail?.capacity === "number" && (
                <span className={styles.heroMetaItem}>
                  👥 {t("clientHero.capacity", { count: educationDetail.capacity })}
                </span>
              )}
              {typeof educationDetail?.durationHours === "number" &&
                educationDetail.durationHours > 0 && (
                  <span className={styles.heroMetaItem}>
                    ⏱ {educationDetail.durationHours}s
                  </span>
                )}
            </div>
          </div>
        </div>

        {/* Workflow stepper */}
        <div className={styles.stepper}>
          {[
            { step: 1, label: "Talep Toplama" },
            { step: 2, label: t("educationManager.step2") },
            { step: 3, label: "Final Planlama" },
          ].map(({ step, label }, idx) => {
            const isActive = step === currentStep;
            const isComplete = step < currentStep;
            return (
              <React.Fragment key={step}>
                <div
                  className={`${styles.stepItem} ${isActive ? styles.stepItemActive : ""} ${isComplete ? styles.stepItemComplete : ""}`}
                >
                  <span className={styles.stepCircle}>
                    {isComplete ? "✓" : step}
                  </span>
                  <span className={styles.stepLabel}>{label}</span>
                </div>
                {idx < 2 && (
                  <div
                    className={`${styles.stepConnector} ${isComplete ? styles.stepConnectorComplete : ""}`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

      </section>

      {showEducationManagerTypeLoading && (
        <div className={styles.pageContent}>
          <div className={styles.loadingSkeleton}>
            <div className={styles.skeletonRow} />
            <div className={styles.skeletonRow} />
            <div className={styles.skeletonRow} />
          </div>
        </div>
      )}

      {showMainWorkflowSection && (
        <>
          <PageHeader
            searchPlaceholder={t("educationOfficer.searchPlaceholder")}
            searchValue={searchTerm}
            onSearch={handleSearch}
            filters={filterFields}
            onFilterChange={handleFilterChange}
            onAddNew={canAddPerson ? handleAddPerson : undefined}
            addButtonLabel={
              canAddPerson
                ? t("educationOfficer.addPerson")
                : undefined
            }
            leftExtraContent={
              educationId && isOnSiteOrOnlineType ? (
                <Button
                  variant="outlined"
                  color="primary"
                  size="medium"
                  type="button"
                  startIcon={<CardView width={18} height={18} />}
                  onClick={() => setIsVirtualClassroomsModalOpen(true)}
                >
                  {t("virtualClassrooms.modal.openButton")}
                </Button>
              ) : null
            }
          />

          <div className={styles.pageContent}>
            <div className={styles.mainLayout}>
              <div className={styles.accordionSection}>
                {isLoading ? (
                  <div className={styles.loading}>{t("loading")}</div>
                ) : (
                  <DepartmentAccordion
                    departments={departments}
                    expandedIds={expandedIds}
                    onToggleExpand={handleToggleExpand}
                    selectedRows={selectedRows}
                    onRowSelect={handleRowSelect}
                    onDepartmentSelectAll={handleDepartmentSelectAll}
                    talepLabel={t("educationManager.talep")}
                    onayLabel={t("educationManager.onay")}
                    selectableStatus="ikinci_onay_bekliyor"
                    columns={
                      canDeleteEnrollment ? managerTableColumns : undefined
                    }
                  />
                )}
              </div>

              <div className={styles.approvalCartColumn}>
            <aside className={styles.approvalCart}>
              <h2 className={styles.approvalCartTitle}>
                {t("educationOfficer.approvalCart")}
              </h2>
              <div className={styles.cartList}>
                {selectedRows.length === 0 ? (
                  <p className={styles.emptyCart}>
                    {t("educationOfficer.emptyCart")}
                  </p>
                ) : (
                  selectedRows.map((row) => (
                    <div key={row.id} className={styles.cartItem}>
                      <div className={styles.cartItemContent}>
                        <span className={styles.cartItemName}>
                          {row.personelAdi}
                        </span>
                        <span className={styles.cartItemTc}>
                          {row.tcKimlikNo}
                        </span>
                      </div>
                      <button
                        type="button"
                        className={styles.cartItemRemove}
                        onClick={() => removeFromCart(row.id)}
                        aria-label={t("common.remove")}
                      >
                        <Close width={20} height={20} color="#707a8f" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </aside>
            <div className={styles.cartFooter}>
              <div className={styles.cartTotalWrap}>
                <span className={styles.cartTotal}>
                  <span className={styles.cartTotalLabel}>
                    {t("educationOfficer.total")}
                  </span>
                  <span className={styles.cartTotalCount}>
                    {selectedRows.length}
                  </span>
                </span>
              </div>
              <div className={styles.cartActions}>
                <Button
                  variant="outlined"
                  color="error"
                  size="medium"
                  className={styles.submitButton}
                  onClick={handleClickReject}
                  disabled={selectedRows.length === 0}
                >
                  {t("educationOfficer.reject")}
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  size="medium"
                  className={styles.submitButton}
                  startIcon={<Check width={20} height={20} color="#fff" />}
                  onClick={handleClickSendForApproval}
                  disabled={selectedRows.length === 0}
                >
                  {t("educationOfficer.sendForApproval")}
                </Button>
              </div>
            </div>
          </div>
        </div>
          </div>
        </>
      )}

      {showFinalizePlanningSection && (() => {
        const pendingPoolCount = availablePlanningDepartments.reduce(
          (sum, d) => sum + d.personnel.length,
          0,
        );
        const assignedCount = groupAssignedEnrollmentIds.size;
        const savedClassCount = savedPlanningGroups.length;
        const draftClassCount = planningGroups.length;
        const excludedCount = unassignedNotInAnyClass.length;

        return (
        <div className={styles.pageContent}>
          <section className={styles.finalPlanningSection}>
            <div className={styles.sectionHeader}>
              <div>
                <h2 className={styles.sectionTitle}>
                  {t("educationManager.finalPlanning")}
                </h2>
                <p className={styles.sectionSubtitle}>
                  {t("educationManager.finalPlanningDesc")}
                </p>
              </div>
              <div className={styles.sectionHeaderActions}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="medium"
                  startIcon={<Plus width={16} height={16} />}
                  className={styles.addGroupButton}
                  onClick={handleAddGroup}
                >
                  {t("educationManager.addGroup")}
                </Button>
              </div>
            </div>

            <div className={styles.planningStats}>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>
                  {t("educationManager.statPending")}
                </span>
                <span className={styles.statValue}>{pendingPoolCount}</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>
                  {t("educationManager.statAssigned")}
                </span>
                <span className={styles.statValue}>{assignedCount}</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>
                  {t("educationManager.statSavedClasses")}
                </span>
                <span className={styles.statValue}>{savedClassCount}</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>
                  {t("educationManager.statDraftClasses")}
                </span>
                <span className={styles.statValue}>{draftClassCount}</span>
              </div>
              <div
                className={`${styles.statCard} ${excludedCount > 0 ? styles.statCardWarning : ""}`}
              >
                <span className={styles.statLabel}>
                  {t("educationManager.statExcluded")}
                </span>
                <span className={styles.statValue}>{excludedCount}</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>
                  {t("educationManager.statRejected")}
                </span>
                <span className={styles.statValue}>
                  {requestStats.rejected}
                </span>
              </div>
            </div>

            <div className={styles.planningLayout}>
              <div className={styles.planningLeft}>
                <div className={styles.panelHeader}>
                  <div className={styles.panelHeaderLeft}>
                    <h3>{t("educationManager.pendingPool")}</h3>
                    <span className={styles.panelCount}>
                      {planningSelectedRows.length}
                    </span>
                  </div>
                  {canAddPerson && (
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={
                        <Plus width={14} height={14} color="#1976d2" />
                      }
                      onClick={handleAddPerson}
                    >
                      {t("educationOfficer.addPerson")}
                    </Button>
                  )}
                </div>
                {isPlanningLoading ? (
                  <div className={styles.loading}>{t("loading")}</div>
                ) : (
                  <DepartmentAccordion
                    departments={availablePlanningDepartments}
                    expandedIds={expandedIds}
                    onToggleExpand={handleToggleExpand}
                    selectedRows={planningSelectedRows}
                    onRowSelect={handlePlanningRowSelect}
                    onDepartmentSelectAll={handlePlanningDepartmentSelectAll}
                    talepLabel={t("educationManager.pending")}
                    onayLabel={t("educationManager.approved")}
                  />
                )}
                <div className={styles.planningActions}>
                  <span className={styles.selectionHint}>
                    {t("educationManager.selected")}:{" "}
                    {planningSelectedRows.length}
                  </span>
                </div>
              </div>

              <div className={styles.planningRight}>
                {(savedPlanningGroups.length > 0 ||
                  isSavedPlanningGroupsLoading) && (
                  <div className={styles.subSectionHeader}>
                    <h3 className={styles.subSectionTitle}>
                      {t("educationManager.savedClassesTitle")}
                    </h3>
                    <span className={styles.panelCount}>
                      {savedPlanningGroups.length}
                    </span>
                  </div>
                )}
                <div className={styles.groupsStack}>
                  {isSavedPlanningGroupsLoading && (
                    <div className={styles.loading}>
                      {t("educationManager.savedGroupsLoading")}
                    </div>
                  )}

                  {savedPlanningGroups.map((saved, savedIdx) => {
                    const dayLabel = (day: number) =>
                      dayOptions.find((o) => o.value === String(day))?.label ??
                      String(day);
                    const formatIsoDate = (iso?: string) => {
                      if (!iso) return "—";
                      const d = new Date(iso);
                      if (Number.isNaN(d.getTime())) return "—";
                      return d.toLocaleDateString("tr-TR", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      });
                    };
                    const virtualClassroomId =
                      getPlanningGroupVirtualClassroomId(saved);
                    const presetMatch = saved.presetLocationId
                      ? presetLocations.find(
                          (l) => l.id === saved.presetLocationId,
                        )
                      : undefined;
                    const locationDisplay = presetMatch
                      ? presetMatch.name
                      : saved.location?.trim() || "—";
                    return (
                      <div
                        key={`saved-planning-${saved.id ?? virtualClassroomId ?? savedIdx}`}
                        className={`${styles.groupCard} ${styles.groupCardDisabled}`}
                      >
                        <div className={styles.savedGroupHeader}>
                          <div className={styles.savedGroupHeaderLeft}>
                            <Chip type="success" variant="soft" size="small">
                              {t("educationManager.savedClassBadge")}
                            </Chip>
                            <h4 className={styles.savedGroupTitle}>
                              {saved.name?.trim() ||
                                t("educationManager.savedClassUntitled")}
                            </h4>
                          </div>
                          {virtualClassroomId ? (
                            <Button
                              variant="outlined"
                              size="small"
                              type="button"
                              onClick={() =>
                                setEditVirtualClassTarget({
                                  group: saved,
                                  virtualClassroomId,
                                })
                              }
                            >
                              {t("educationManager.editSavedClass")}
                            </Button>
                          ) : null}
                        </div>
                        {saved.jitsiRoomName?.trim() ? (
                          <div className={styles.readOnlyField}>
                            <span className={styles.readOnlyLabel}>
                              {t("educationManager.jitsiRoomName")}
                            </span>
                            <span className={styles.readOnlyValue}>
                              {saved.jitsiRoomName}
                            </span>
                          </div>
                        ) : null}
                        <div className={styles.readOnlyField}>
                          <span className={styles.readOnlyLabel}>
                            {t("educationManager.groupDate")}
                          </span>
                          <span className={styles.readOnlyValue}>
                            {formatIsoDate(saved.startDate)} —{" "}
                            {formatIsoDate(saved.endDate)}
                          </span>
                        </div>
                        <div className={styles.readOnlyField}>
                          <span className={styles.readOnlyLabel}>
                            {t("educationManager.groupLocation")}
                          </span>
                          <span className={styles.readOnlyValue}>
                            {locationDisplay}
                          </span>
                        </div>
                        {(saved.scheduledTimes?.length ?? 0) > 0 ? (
                          <div className={styles.savedScheduleBlock}>
                            <span className={styles.readOnlyLabel}>
                              {t("educationManager.schedule")}
                            </span>
                            <ul className={styles.savedScheduleList}>
                              {(saved.scheduledTimes ?? []).map((st, i) => (
                                <li key={i}>
                                  {dayLabel(st.day)} {st.startTime} –{" "}
                                  {st.endTime}
                                  {st.timezone ? ` (${st.timezone})` : ""}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : null}
                        <div className={styles.groupMembers}>
                          <div className={styles.groupMembersHeader}>
                            <span>
                              {t("educationManager.groupMembers")} (
                              {saved.enrollmentIds?.length ?? 0})
                            </span>
                          </div>
                          <div className={styles.groupMembersList}>
                            {saved.enrollments && saved.enrollments.length > 0
                              ? saved.enrollments.map((en) => {
                                  const fullName = [en.firstName, en.lastName]
                                    .filter(Boolean)
                                    .join(" ")
                                    .trim();
                                  return (
                                    <div
                                      key={en.enrollmentId}
                                      className={styles.groupMemberItem}
                                    >
                                      <div className={styles.savedMemberLine}>
                                        <span
                                          className={styles.savedMemberName}
                                        >
                                          {fullName || en.enrollmentId}
                                        </span>
                                        {en.title ? (
                                          <span
                                            className={styles.savedMemberTitle}
                                          >
                                            {en.title}
                                          </span>
                                        ) : null}
                                      </div>
                                    </div>
                                  );
                                })
                              : (saved.enrollmentIds ?? []).map((id) => {
                                  const person = planningPersonnelMap.get(id);
                                  return (
                                    <div
                                      key={id}
                                      className={styles.groupMemberItem}
                                    >
                                      <span>{person?.personelAdi ?? id}</span>
                                    </div>
                                  );
                                })}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {planningGroups.length === 0 &&
                  savedPlanningGroups.length === 0 &&
                  !isSavedPlanningGroupsLoading ? (
                    <div className={styles.emptyState}>
                      <p className={styles.emptyTitle}>
                        {t("educationManager.noGroups")}
                      </p>
                      <p className={styles.emptySubtitle}>
                        {t("educationManager.noGroupsHint")}
                      </p>
                      <Button
                        variant="contained"
                        color="primary"
                        size="medium"
                        startIcon={<Plus width={16} height={16} color="#fff" />}
                        onClick={handleAddGroup}
                      >
                        {t("educationManager.addGroup")}
                      </Button>
                    </div>
                  ) : null}
                </div>

                {planningGroups.length > 0 && (
                  <div className={styles.subSectionHeader}>
                    <h3 className={styles.subSectionTitle}>
                      {t("educationManager.draftClassesTitle")}
                    </h3>
                    <span className={styles.panelCount}>
                      {planningGroups.length}
                    </span>
                  </div>
                )}
                <div className={styles.groupsStack}>
                  {planningGroups.map((group) => {
                      const isPresetSelected =
                        group.presetLocationId.trim().length > 0;
                      return (
                        <div key={group.id} className={styles.groupCard}>
                          <div className={styles.groupHeader}>
                            <Input
                              label={t("educationManager.groupName")}
                              placeholder={t("educationManager.groupNamePlaceholder")}
                              value={group.name}
                              onChange={(e) =>
                                updateGroup(group.id, (prev) => ({
                                  ...prev,
                                  name: e.target.value,
                                }))
                              }
                            />
                            <button
                              type="button"
                              className={styles.groupRemove}
                              onClick={() => handleRemoveGroup(group.id)}
                              aria-label={t("common.remove")}
                            >
                              <Close width={16} height={16} />
                            </button>
                          </div>
                          {educationType === EDUCATION_TYPE_API.ONLINE && (
                            <div className={styles.groupMetaRow}>
                              <Input
                                label={t("educationManager.jitsiRoomName")}
                                placeholder="EduStream-Kamu-Mevzuati"
                                value={group.jitsiRoomName}
                                onChange={(e) =>
                                  updateGroup(group.id, (prev) => ({
                                    ...prev,
                                    jitsiRoomName: sanitizeJitsiRoomName(e.target.value),
                                  }))
                                }
                              />
                            </div>
                          )}
                          <div className={styles.groupGrid}>
                            <DatePicker
                              label={t("educationManager.groupDate")}
                              isRange
                              value={group.dateRange}
                              onChange={(value) =>
                                updateGroup(group.id, (prev) => ({
                                  ...prev,
                                  dateRange: Array.isArray(value)
                                    ? value
                                    : [value, value],
                                }))
                              }
                            />
                            <Dropdown
                              label={t(
                                "educationManager.virtualClassroomInstructorsLabel",
                              )}
                              placeholder={t(
                                "educationManager.virtualClassroomInstructorsPlaceholder",
                              )}
                              options={educationInstructorOptions}
                              value={group.instructorIds}
                              multiple
                              searchable
                              onSelect={(value) => {
                                const next = Array.isArray(value)
                                  ? value
                                  : value
                                    ? [value]
                                    : [];
                                updateGroup(group.id, (prev) => ({
                                  ...prev,
                                  instructorIds: next,
                                }));
                              }}
                              selectedLabel={educationInstructorOptions
                                .filter((o) =>
                                  group.instructorIds.includes(o.value),
                                )
                                .map((o) => o.label)
                                .join(", ")}
                            />
                            {educationType !== EDUCATION_TYPE_API.ONLINE && (
                              <Dropdown
                                label={t("educationManager.groupPresetLocation")}
                                placeholder={t("educationManager.groupPresetLocationPlaceholder")}
                                options={presetLocationOptions}
                                value={group.presetLocationId}
                                selectedLabel={
                                  presetLocationOptions.find(
                                    (o) => o.value === group.presetLocationId,
                                  )?.label ?? ""
                                }
                                onSelect={(value) => {
                                  const id = Array.isArray(value)
                                    ? (value[0] ?? "")
                                    : value;
                                  const selected = presetLocations.find(
                                    (l) => l.id === id,
                                  );
                                  if (selected) {
                                    updateGroup(group.id, (prev) => ({
                                      ...prev,
                                      presetLocationId: selected.id,
                                      location: selected.address ?? "",
                                      latitude:
                                        selected.latitude != null
                                          ? String(selected.latitude)
                                          : prev.latitude,
                                      longitude:
                                        selected.longitude != null
                                          ? String(selected.longitude)
                                          : prev.longitude,
                                    }));
                                  }
                                }}
                                searchable
                              />
                            )}
                            {educationType !== EDUCATION_TYPE_API.ONLINE &&
                              isPresetSelected && group.location && (
                              <div className={styles.readOnlyField}>
                                <span className={styles.readOnlyLabel}>
                                  {t("educationManager.groupAddress")}
                                </span>
                                <span className={styles.readOnlyValue}>
                                  {group.location}
                                </span>
                              </div>
                            )}
                            {educationType !== EDUCATION_TYPE_API.ONLINE &&
                              isPresetSelected &&
                              group.latitude &&
                              group.longitude && (
                                <div className={styles.mapField}>
                                  <label className={styles.fieldLabel}>
                                    {t("educationManager.groupMap")}
                                  </label>
                                  <MapComponent
                                    center={{
                                      lat: Number(group.latitude) || 41.0082,
                                      lng: Number(group.longitude) || 28.9784,
                                    }}
                                    zoom={14}
                                    height="220px"
                                    selectedLocation={{
                                      lat: Number(group.latitude),
                                      lng: Number(group.longitude),
                                      address: group.location || "",
                                    }}
                                  />
                                </div>
                              )}
                          </div>

                          <div className={styles.scheduleSection}>
                            <div className={styles.scheduleHeader}>
                              <h4>
                                {t("educationManager.schedule")}
                              </h4>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleAddScheduledTime(group.id)}
                              >
                                {t("educationManager.addSchedule")}
                              </Button>
                            </div>
                            {group.scheduledTimes.length === 0 ? (
                              <p className={styles.emptySchedule}>
                                {t("educationManager.scheduleEmpty")}
                              </p>
                            ) : (
                              group.scheduledTimes.map((time) => (
                                <div
                                  key={time.id}
                                  className={styles.scheduleRow}
                                >
                                  <Dropdown
                                    label={t("educationManager.day")}
                                    options={dayOptions}
                                    value={String(time.day)}
                                    selectedLabel={
                                      dayOptions.find(
                                        (o) => o.value === String(time.day),
                                      )?.label
                                    }
                                    onSelect={(value) =>
                                      handleUpdateScheduledTime(
                                        group.id,
                                        time.id,
                                        { day: Number(value) },
                                      )
                                    }
                                  />
                                  <Input
                                    type="time"
                                    label={t("educationManager.startTime")}
                                    value={time.startTime}
                                    onChange={(e) =>
                                      handleUpdateScheduledTime(
                                        group.id,
                                        time.id,
                                        { startTime: e.target.value },
                                      )
                                    }
                                  />
                                  <Input
                                    type="time"
                                    label={t("educationManager.endTime")}
                                    value={time.endTime}
                                    onChange={(e) =>
                                      handleUpdateScheduledTime(
                                        group.id,
                                        time.id,
                                        { endTime: e.target.value },
                                      )
                                    }
                                  />
                                  <button
                                    type="button"
                                    className={styles.scheduleRemove}
                                    onClick={() =>
                                      handleRemoveScheduledTime(
                                        group.id,
                                        time.id,
                                      )
                                    }
                                  >
                                    <Close width={16} height={16} />
                                  </button>
                                </div>
                              ))
                            )}
                          </div>

                          <div className={styles.groupMembers}>
                            <div className={styles.groupMembersHeader}>
                              <span>
                                {t("educationManager.groupMembers")}{" "}
                                ({group.enrollmentIds.length}/20)
                              </span>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<Check width={14} height={14} />}
                                className={styles.addSelectedButton}
                                onClick={() =>
                                  handleAddSelectedToGroup(group.id)
                                }
                                disabled={planningSelectedRows.length === 0}
                              >
                                {t("educationManager.addSelected")}
                              </Button>
                            </div>
                            {group.enrollmentIds.length === 0 ? (
                              <p className={styles.emptyGroupMembers}>
                                {t("educationManager.noMembers")}
                              </p>
                            ) : (
                              <div className={styles.groupMembersList}>
                                {group.enrollmentIds.map((id) => {
                                  const person = planningPersonnelMap.get(id);
                                  return (
                                    <div
                                      key={id}
                                      className={styles.groupMemberItem}
                                    >
                                      <span>{person?.personelAdi ?? id}</span>
                                      <button
                                        type="button"
                                        className={styles.memberRemove}
                                        onClick={() =>
                                          handleRemoveFromGroup(group.id, id)
                                        }
                                        aria-label={t("common.remove")}
                                      >
                                        <Close width={16} height={16} />
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          {group.enrollmentIds.length > 0 && (
                            <div className={styles.groupFooter}>
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => handleSaveSingleGroup(group)}
                                disabled={savingGroupId === group.id}
                              >
                                {savingGroupId === group.id
                                  ? t("common.loading")
                                  : t("educationManager.saveGroup")}
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>

                <div
                  className={`${styles.excludedCard} ${excludedCount > 0 ? styles.excludedCardWarning : ""}`}
                >
                  <div className={styles.panelHeader}>
                    <h3>
                      {t("educationManager.excluded")}
                    </h3>
                    <span className={styles.panelCount}>{excludedCount}</span>
                  </div>
                  {excludedCount === 0 ? (
                    <p className={styles.emptyGroupMembers}>
                      {t("educationManager.excludedAllAssigned")}
                    </p>
                  ) : (
                    <>
                      <p className={styles.excludedHint}>
                        {t("educationManager.excludedDynamicHint", { count: excludedCount })}
                      </p>
                      <div className={styles.groupMembersList}>
                        {unassignedNotInAnyClass.map((row) => (
                          <div key={row.id} className={styles.groupMemberItem}>
                            <span>{row.personelAdi}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.planningStickyFooter}>
              <div className={styles.planningStickyFooterInfo}>
                {excludedCount > 0 ? (
                  <span className={styles.planningStickyFooterWarning}>
                    {t("educationManager.publishedHint", { count: excludedCount })}
                  </span>
                ) : (
                  <span className={styles.planningStickyFooterHint}>
                    {t("educationManager.publishedAllAssigned")}
                  </span>
                )}
              </div>
              <Button
                variant="contained"
                color="primary"
                size="medium"
                onClick={handleFinalizePlanning}
                disabled={isPlanningSubmitting}
              >
                {isPlanningSubmitting
                  ? t("common.loading")
                  : t("educationManager.finalize")}
              </Button>
            </div>
          </section>
        </div>
        );
      })()}

      <AddPersonModal
        isOpen={isAddPersonOpen}
        onClose={() => setIsAddPersonOpen(false)}
        educationId={educationId ?? null}
        onAdded={() => {
          fetchDepartments();
          fetchPlanningDepartments();
        }}
      />

      <ConfirmModal
        isOpen={isConfirmSendOpen}
        onClose={() => !isSendLoading && setIsConfirmSendOpen(false)}
        onConfirm={handleConfirmSendForApproval}
        title={t("educationOfficer.confirmSend.title")}
        message={
          <>
            {t("educationOfficer.confirmSend.messagePrefix")}{" "}
            <span className={styles.confirmModalCount}>
              {selectedRows.length}{" "}
              {t("educationOfficer.confirmSend.adet")}
            </span>{" "}
            {t("educationOfficer.confirmSend.messageSuffix")}
          </>
        }
        confirmText={t("educationOfficer.confirmSend.confirm")}
        cancelText={t("educationOfficer.confirmSend.cancel")}
        loading={isSendLoading}
      />
      <ConfirmModal
        isOpen={isConfirmRejectOpen}
        onClose={() => !isSendLoading && setIsConfirmRejectOpen(false)}
        onConfirm={handleConfirmReject}
        title={t("educationOfficer.confirmReject.title")}
        message={
          <>
            {t("educationOfficer.confirmReject.messagePrefix")}{" "}
            <span className={styles.confirmModalCount}>
              {selectedRows.length}{" "}
              {t("educationOfficer.confirmReject.adet")}
            </span>{" "}
            {t("educationOfficer.confirmReject.messageSuffix")}
          </>
        }
        confirmText={t("educationOfficer.confirmReject.confirm")}
        cancelText={t("educationOfficer.confirmReject.cancel")}
        loading={isSendLoading}
        confirmVariant="danger"
      />
      <ConfirmModal
        isOpen={pendingDeleteRow !== null}
        onClose={() => !isDeleteLoading && setPendingDeleteRow(null)}
        onConfirm={handleConfirmDeleteEnrollment}
        title={t("educationManager.deleteEnrollmentConfirmTitle")}
        message={
          pendingDeleteRow ? (
            <>
              <span className={styles.confirmModalPersonName}>
                {pendingDeleteRow.personelAdi}
              </span>
              {t("educationManager.deleteEnrollmentConfirmMessage")}
            </>
          ) : null
        }
        confirmText={t("educationManager.deleteEnrollmentConfirm")}
        cancelText={t("common.cancel")}
        loading={isDeleteLoading}
        confirmVariant="danger"
      />

      {educationId ? (
        <VirtualClassroomsListModal
          isOpen={isVirtualClassroomsModalOpen}
          onClose={() => setIsVirtualClassroomsModalOpen(false)}
          educationId={educationId}
          subtitle={educationName}
        />
      ) : null}

      <EditVirtualClassroomModal
        isOpen={editVirtualClassTarget !== null}
        onClose={() => setEditVirtualClassTarget(null)}
        virtualClassroomId={
          editVirtualClassTarget?.virtualClassroomId ?? ""
        }
        educationId={educationId ?? undefined}
        group={editVirtualClassTarget?.group ?? null}
        educationType={educationType}
        presetLocations={presetLocations}
        presetLocationOptions={presetLocationOptions}
        dayOptions={dayOptions}
        canViewLocations={canViewLocations}
        addablePersonnel={unassignedNotInAnyClass}
        onSaved={() => {
          fetchSavedPlanningGroups();
          fetchPlanningDepartments();
        }}
      />
    </DashboardLayout>
  );
};

export default EducationManagerPage;
